import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { AgencyCampaignSection } from '../../../../agency/model';
import { SupplyTab } from '../../supplyRouteSearch';
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import {
  DashboardPageFormActions,
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormShell,
} from '@shigo-ui/dashboard-ui';
import { Button, Form, Input, Skeleton, Textarea, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { dashboardT } from '../../../../../lib/messages';
import { returnFallback, useReturnNavigation } from '../../../../../lib/returnNavigation';
import { useSupplyMutations, useTailoredSupplyQuery } from '../../hooks';
import { supplyAppSchema, supplyDomainSchema, type TailoredSupplyDetail } from '../../model';
import {
  serializeTailoredSupplyFormValue,
  tailoredSupplyFormSchema,
  type TailoredSupplyFormValue,
} from '../../formSchemas';
import { ManageAppsDialog, ManageDomainsDialog } from './ManageSupplySelectionDialog';
import { SelectedAppsSection, SelectedDomainsSection } from './TailoredSupplySelectionSections';

export function TailoredSupplyEditorPage({ tailoredSupplyId }: { tailoredSupplyId?: string }) {
  const supplyFallback = useSupplyFallback();
  const { returnTo } = useReturnNavigation(supplyFallback);
  const detail = useTailoredSupplyQuery(tailoredSupplyId);
  const loadingDetail = Boolean(tailoredSupplyId) && !detail.isFetchedAfterMount && !detail.isError;
  const goBack = () => void returnTo();
  let content = (
    <TailoredSupplyEditorForm
      key={tailoredSupplyId ?? 'create'}
      detail={detail.data}
      onCancel={goBack}
    />
  );
  if (tailoredSupplyId && detail.isError)
    content = (
      <div className="grid gap-4 p-5">
        <p role="alert" className="text-destructive text-sm">
          {dashboardT('supply.loadError')}
        </p>
        <div>
          <Button type="button" variant="outline" onClick={goBack}>
            {dashboardT('supply.back')}
          </Button>
        </div>
      </div>
    );
  else if (loadingDetail)
    content = (
      <div className="grid gap-3 p-5">
        <Skeleton className="h-9" />
        <Skeleton className="h-24" />
        <Skeleton className="h-40" />
      </div>
    );
  else if (tailoredSupplyId && !detail.data)
    content = (
      <div className="grid gap-4 p-5">
        <p role="alert" className="text-destructive text-sm">
          {dashboardT('supply.loadError')}
        </p>
        <div>
          <Button type="button" variant="outline" onClick={goBack}>
            {dashboardT('supply.back')}
          </Button>
        </div>
      </div>
    );
  return (
    <DashboardPageFormShell
      title={
        tailoredSupplyId ? dashboardT('supply.tailored.edit') : dashboardT('supply.tailored.create')
      }
      backAction={<ReturnBackLink fallback={supplyFallback} label={dashboardT('supply.back')} />}
    >
      {content}
    </DashboardPageFormShell>
  );
}

function TailoredSupplyEditorForm({
  detail,
  onCancel,
}: {
  detail?: TailoredSupplyDetail | undefined;
  onCancel: () => void;
}) {
  'use no memo';

  const { returnTo } = useReturnNavigation(useSupplyFallback());
  const mutations = useSupplyMutations();
  const initialValue = {
    name: detail?.name ?? '',
    description: detail?.description ?? '',
    apps: detail?.apps ?? [],
    domains: detail?.domains ?? [],
  };
  const form = useForm<TailoredSupplyFormValue>({
    defaultValues: initialValue,
    resolver: zodResolver(tailoredSupplyFormSchema),
  });
  const { errors } = useFormState({ control: form.control });
  const apps = useWatch({ control: form.control, name: 'apps' });
  const domains = useWatch({ control: form.control, name: 'domains' });
  const [manageAppsOpen, setManageAppsOpen] = React.useState(false);
  const [manageDomainsOpen, setManageDomainsOpen] = React.useState(false);
  const nameId = React.useId();
  const descriptionId = React.useId();
  const pending =
    mutations.createTailoredSupply.isPending || mutations.updateTailoredSupply.isPending;
  async function submit(values: TailoredSupplyFormValue) {
    const payload = serializeTailoredSupplyFormValue(values, detail?.id);
    try {
      if (detail?.id) await mutations.updateTailoredSupply.mutateAsync({ id: detail.id, payload });
      else await mutations.createTailoredSupply.mutateAsync(payload);
    } catch {
      form.setError('root.server', { message: dashboardT('supply.saveError') });
      return;
    }

    toast.success(dashboardT(detail?.id ? 'supply.tailored.updated' : 'supply.tailored.created'));
    try {
      await returnTo({ replace: true });
    } catch {
      toast.error(dashboardT('supply.redirectError'));
    }
  }
  return (
    <>
      <Form {...form}>
        <form noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
          <DashboardPageFormBody
            width="lg"
            className="gap-6 px-4 py-6 md:px-5 md:py-8"
            title={dashboardT('supply.details')}
            description={dashboardT('supply.detailsDescription')}
          >
            <div className="grid gap-4">
              <DashboardPageFormField
                label={dashboardT('supply.name')}
                htmlFor={nameId}
                required
                error={errors.name?.message}
              >
                <Input
                  id={nameId}
                  className="h-9"
                  maxLength={255}
                  aria-invalid={Boolean(errors.name)}
                  {...form.register('name')}
                />
              </DashboardPageFormField>
              <DashboardPageFormField
                label={dashboardT('supply.description')}
                htmlFor={descriptionId}
              >
                <Textarea id={descriptionId} {...form.register('description')} />
              </DashboardPageFormField>
            </div>
            <SelectedAppsSection
              items={apps}
              onManage={() => {
                setManageAppsOpen(true);
              }}
              onRemove={(id) => {
                form.setValue(
                  'apps',
                  apps.filter((item) => item.id !== id),
                  { shouldDirty: true },
                );
              }}
              onClear={() => {
                form.setValue('apps', [], { shouldDirty: true });
              }}
            />
            <SelectedDomainsSection
              items={domains}
              onManage={() => {
                setManageDomainsOpen(true);
              }}
              onRemove={(id) => {
                form.setValue(
                  'domains',
                  domains.filter((item) => item.id !== id),
                  { shouldDirty: true },
                );
              }}
              onClear={() => {
                form.setValue('domains', [], { shouldDirty: true });
              }}
            />
            {errors.apps?.message ? (
              <p role="alert" className="text-destructive text-sm">
                {errors.apps.message}
              </p>
            ) : null}
            {errors.root?.server?.message ? (
              <p role="alert" className="text-destructive text-sm">
                {errors.root.server.message}
              </p>
            ) : null}
          </DashboardPageFormBody>
          <DashboardPageFormActions>
            <Button
              type="button"
              variant="outline"
              className="rounded-full px-5"
              onClick={onCancel}
            >
              {dashboardT('supply.cancel')}
            </Button>
            <Button type="submit" className="rounded-full px-5" disabled={pending}>
              {dashboardT('supply.save')}
            </Button>
          </DashboardPageFormActions>
        </form>
      </Form>
      <ManageAppsDialog
        open={manageAppsOpen}
        selectedItems={apps.map((app) => supplyAppSchema.parse(app))}
        onOpenChange={setManageAppsOpen}
        onConfirm={(apps) => {
          form.setValue('apps', apps, { shouldDirty: true, shouldValidate: true });
        }}
      />
      <ManageDomainsDialog
        open={manageDomainsOpen}
        selectedItems={domains.map((domain) => supplyDomainSchema.parse(domain))}
        onOpenChange={setManageDomainsOpen}
        onConfirm={(domains) => {
          form.setValue('domains', domains, { shouldDirty: true, shouldValidate: true });
        }}
      />
    </>
  );
}

function useSupplyFallback() {
  const scope = useAgencyCampaignContext();
  return scope
    ? returnFallback({
        to: '/a/$agencyId/campaigns/$section',
        params: { agencyId: scope.agency.id, section: AgencyCampaignSection.Supply },
        search: { supplyTab: SupplyTab.Tailored },
      })
    : returnFallback({ to: '/campaigns/supply/tailored-supply' });
}
